import { ACCOUNT_SEARCH_LIMIT } from '@orn/global/admin/accountSearch.js';

import {
  AccountMatchField,
  AdminInboundChannel,
  AdminOutreachChannel,
  CaregiverRelationship,
  PartnerRelationship,
  PaymentMethodStatus,
  PaymentMethodType,
  ResidenceType,
  SmsConsentState,
  SubscriptionStatus,
  UserStatus,
  FamilyRole,
} from '../../__generated__/globalTypes';
import { PATHS, userToolPath } from '../../paths';

import type {
  FinancialAccountPrimaryType,
  FinancialAccountSubtype,
  IdentityMatchResult,
  MandateRevocationChannel,
  SubscriptionInterval,
} from '../../__generated__/globalTypes';

/** Which field the hit came from, phrased for the result row's hover text. */
export const MATCH_FIELD_LABELS: Record<AccountMatchField, string> = {
  [AccountMatchField.UserEmail]: 'email',
  [AccountMatchField.UserName]: 'name',
  [AccountMatchField.FamilyName]: 'household',
};

/**
 * Statuses that still entitle the household, which is what the badge colors on.
 * The authoritative list is `ENTITLING_SUBSCRIPTION_STATUSES` in `@orn/core`,
 * which the browser bundle cannot import, so a drift here is a wrong color
 * rather than a wrong access decision.
 *
 * `CREATING` is deliberately absent: a household stuck mid-checkout is exactly
 * what an operator is looking for, and coloring it as entitled would render a
 * failed checkout as a working plan.
 */
export const ENTITLED_BADGE_STATUSES: readonly SubscriptionStatus[] = [
  SubscriptionStatus.Trialing,
  SubscriptionStatus.Active,
  SubscriptionStatus.PastDue,
];

/** Whether the badge should read as a household that currently has access. */
export function isEntitledStatus(status: SubscriptionStatus): boolean {
  return ENTITLED_BADGE_STATUSES.includes(status);
}

/** The badge's text — the enum's own spelling, with the underscores opened up. */
export function formatSubscriptionStatus(status: SubscriptionStatus): string {
  return formatEnumLabel(status);
}

/**
 * Whether the search hit its cap and is therefore hiding households.
 *
 * Exactly `ACCOUNT_SEARCH_LIMIT` rows is indistinguishable from a complete
 * result set of that size, so this reports the ambiguous case as truncated:
 * telling an operator to narrow a search that happened to return exactly fifty
 * costs them a keystroke, while staying quiet on a truncated one costs them
 * the household they were looking for.
 */
export function isSearchTruncated(resultCount: number): boolean {
  return resultCount >= ACCOUNT_SEARCH_LIMIT;
}

/** An enum member's own spelling, with the underscores opened up. */
function formatEnumLabel(value: string): string {
  return value.replace(/_/g, ' ');
}

/**
 * A person as `First "Preferred" Last`, so one line answers both the name a
 * document carries and the name the assistant uses.
 *
 * Every part is optional and each drops out on its own — a preferred name
 * equal to the first name still shows twice, because the operator's question
 * is what the record holds, not what reads nicely. `fallback` covers a record
 * with no name at all, where the caller has an email to show instead.
 */
export function formatPersonName(
  person: {
    // Codegen renders a nullable field as `T | null | undefined`; both readings
    // mean "unset" here.
    firstName?: string | null;
    lastName?: string | null;
    preferredName?: string | null;
  },
  fallback: string
): string {
  const quoted = person.preferredName?.trim();
  const parts = [person.firstName?.trim(), quoted ? `"${quoted}"` : null, person.lastName?.trim()].filter(
    (part): part is string => part !== null && part !== undefined && part !== ''
  );
  return parts.length === 0 ? fallback : parts.join(' ');
}

/**
 * The household's name as an operator says it out loud. The column is
 * defaulted from the caregiver's surname, so the noun has to come from here
 * rather than from the data — appending it is what keeps "Bowers" from
 * reading as a person.
 *
 * The name is editable, though, so a household that has already named itself
 * one keeps the name it chose rather than earning a second noun.
 */
export function formatHouseholdName(name: string | null): string | null {
  const trimmed = name?.trim();
  if (!trimmed) {
    return null;
  }
  return /household$/i.test(trimmed) ? trimmed : `${trimmed} Household`;
}

/** The role badge's text. */
export function formatFamilyRole(role: FamilyRole): string {
  return formatEnumLabel(role);
}

/** The account-status badge's text. */
export function formatUserStatus(status: UserStatus): string {
  return formatEnumLabel(status);
}

/**
 * What an operator calls each channel, which is not what the column is called.
 * `APP` is the web app, and an operator looking for "when did they last write
 * from the web" would not find a row labelled "app".
 */
export const INBOUND_CHANNEL_LABELS: Record<AdminInboundChannel, string> = {
  [AdminInboundChannel.App]: 'web',
  [AdminInboundChannel.Sms]: 'sms',
  [AdminInboundChannel.Email]: 'email',
};

/**
 * `NONE` reads as "not asked" rather than as a refusal — the two lead an
 * operator to different next steps.
 */
const SMS_CONSENT_LABELS: Record<SmsConsentState, string> = {
  [SmsConsentState.Granted]: 'opted in',
  [SmsConsentState.Revoked]: 'opted out',
  [SmsConsentState.None]: 'not asked',
};

/** The member's SMS consent in words, or null when there is no number to hold consent for. */
export function formatSmsConsent(state: SmsConsentState | null): string | null {
  return state === null ? null : SMS_CONSENT_LABELS[state];
}

/**
 * The outreach window and daily cap as one line. The zone the window is read
 * in gets its own row, so it is not repeated here.
 */
export function formatOutreachPolicy(policy: {
  outreachActiveHoursStart: string;
  outreachActiveHoursEnd: string;
  outreachDailyCap: number;
}): string {
  return `${policy.outreachActiveHoursStart}–${policy.outreachActiveHoursEnd} · max ${policy.outreachDailyCap}/day`;
}

/** What an operator calls each channel the assistant can reach someone on. */
export const OUTREACH_CHANNEL_LABELS: Record<AdminOutreachChannel, string> = {
  [AdminOutreachChannel.Sms]: 'sms',
  [AdminOutreachChannel.Email]: 'email',
};

/**
 * The open channels as one phrase, or null when nothing can reach the member —
 * which the caller renders as absent rather than as a channel named "none".
 */
export function formatOutreachChannels(channels: readonly AdminOutreachChannel[]): string | null {
  return channels.length === 0 ? null : channels.map((channel) => OUTREACH_CHANNEL_LABELS[channel]).join(', ');
}

/**
 * Delivered outreach over the trailing window, or null when nothing landed.
 *
 * Both counts are shown whenever either is non-zero: "3 sms · 0 email" says
 * the assistant is reaching this member and only by text, which dropping the
 * zero would hide.
 */
export function formatOutreachHistory(history: {
  windowDays: number;
  smsDelivered: number;
  emailDelivered: number;
}): string | null {
  if (history.smsDelivered === 0 && history.emailDelivered === 0) {
    return null;
  }
  return `${history.smsDelivered} sms · ${history.emailDelivered} email · last ${history.windowDays} days`;
}

/**
 * A caregiver relationship in words, including the qualifiers that change what
 * it means. `OTHER` carries its own free text, so the enum name would be the
 * least informative thing we could show.
 */
export function formatCaregiverRelationship(relationship: {
  relationship: CaregiverRelationship;
  // Codegen renders a nullable field as `T | null | undefined`; both readings
  // mean "unset" here, so the formatters take the wider type rather than
  // making every call site normalize.
  relationshipOther?: string | null;
  isStep: boolean;
  isInLaw: boolean;
}): string {
  const base =
    relationship.relationship === CaregiverRelationship.Other
      ? (relationship.relationshipOther?.trim() ?? '') || formatEnumLabel(CaregiverRelationship.Other)
      : formatEnumLabel(relationship.relationship);
  const qualifiers = [relationship.isStep ? 'step' : null, relationship.isInLaw ? 'in-law' : null].filter(
    (part): part is string => part !== null
  );
  return qualifiers.length === 0 ? base : `${base} (${qualifiers.join(', ')})`;
}

/** A loved-one partnership in words, with `OTHER`'s free text standing in for the enum. */
export function formatPartnerRelationship(partnership: {
  relationship: PartnerRelationship;
  relationshipOther?: string | null;
}): string {
  if (partnership.relationship === PartnerRelationship.Other) {
    return (partnership.relationshipOther?.trim() ?? '') || formatEnumLabel(PartnerRelationship.Other);
  }
  return formatEnumLabel(partnership.relationship);
}

/**
 * A vehicle as one line. Every part is nullable, so a row with nothing but an
 * ownership answer still has to render as something an operator can read.
 */
export function formatVehicle(vehicle: { year?: number | null; make?: string | null; model?: string | null }): string {
  const parts = [
    vehicle.year === null || vehicle.year === undefined ? null : String(vehicle.year),
    vehicle.make,
    vehicle.model,
  ].filter((part): part is string => part !== null && part !== undefined && part.trim() !== '');
  return parts.length === 0 ? 'Unspecified vehicle' : parts.join(' ');
}

/** How a possession is held, with `OTHER`'s free text preferred over the enum name. */
export function formatOwnership(ownership?: string | null, ownershipOther?: string | null): string | null {
  if (ownership === null || ownership === undefined) {
    return null;
  }
  if (ownership === 'OTHER') {
    return (ownershipOther?.trim() ?? '') || 'other';
  }
  return formatEnumLabel(ownership).toLowerCase();
}

/**
 * A residence's kind, with `OTHER`'s free text preferred over the enum name.
 * Separate from {@link formatOwnership} because the two answer different
 * questions — what the place is, versus how it is held.
 */
export function formatResidenceType(residenceType: ResidenceType, residenceTypeOther?: string | null): string {
  if (residenceType === ResidenceType.Other) {
    return (residenceTypeOther?.trim() ?? '') || formatEnumLabel(ResidenceType.Other);
  }
  return formatEnumLabel(residenceType);
}

export type ConnectionHealthTone = 'error' | 'warning' | 'success';

export interface IConnectionHealth {
  label: string;
  tone: ConnectionHealthTone;
}

/**
 * A bank connection's health from what is stored, the most actionable first.
 *
 * Reconnection leads because it is what the member has already been asked to
 * do. The healthy state reads "synced", not the Bank connections tool's
 * "healthy": that tool earns the word with a live Plaid check, and this panel
 * makes none, so a connection Plaid has broken but nothing has yet flagged
 * would otherwise be vouched for.
 */
export function formatConnectionHealth(connection: {
  needsReconnection: boolean;
  neverSynced: boolean;
  stale: boolean;
}): IConnectionHealth {
  if (connection.needsReconnection) {
    return { label: 'needs reconnection', tone: 'error' };
  }
  if (connection.neverSynced) {
    return { label: 'never synced', tone: 'warning' };
  }
  if (connection.stale) {
    return { label: 'stale', tone: 'warning' };
  }
  return { label: 'synced', tone: 'success' };
}

/**
 * The subtype when there is one, else the broad type — "CREDIT CARD" says what
 * "CREDIT · CREDIT CARD" says, and a subtype-less account still reads as
 * something.
 */
export function formatAccountKind(type: FinancialAccountPrimaryType, subtype?: FinancialAccountSubtype | null): string {
  return formatEnumLabel(subtype ?? type);
}

/**
 * The Plaid products a connection was granted, or null when the list has never
 * been read from Plaid — a different answer from an empty list, which a fetch
 * did return, so the caller renders the two differently.
 */
export function formatConsentedProducts(products?: readonly string[] | null): string | null {
  if (products === null || products === undefined) {
    return null;
  }
  return products.length === 0 ? 'none' : products.join(', ');
}

/** The payment rail, as a row label. */
export const PAYMENT_METHOD_TYPE_LABELS: Record<PaymentMethodType, string> = {
  [PaymentMethodType.UsBankAccount]: 'Bank account',
  [PaymentMethodType.Card]: 'Card',
};

export type PaymentMethodTone = ConnectionHealthTone | 'neutral';

// `DETACHED` is neutral rather than an alarm: it is how a replaced method ends.
const PAYMENT_METHOD_STATUS_TONES: Record<PaymentMethodStatus, PaymentMethodTone> = {
  [PaymentMethodStatus.Active]: 'success',
  [PaymentMethodStatus.VerificationPending]: 'warning',
  [PaymentMethodStatus.Errored]: 'error',
  [PaymentMethodStatus.Detached]: 'neutral',
};

/** A payment method's status badge: its word and its tone. */
export function formatPaymentMethodStatus(status: PaymentMethodStatus): { label: string; tone: PaymentMethodTone } {
  return { label: formatEnumLabel(status), tone: PAYMENT_METHOD_STATUS_TONES[status] };
}

/** The billing interval, with a scheduled switch appended: "MONTHLY → ANNUAL". */
export function formatSubscriptionInterval(subscription: {
  interval: SubscriptionInterval;
  pendingInterval?: SubscriptionInterval | null;
}): string {
  const current = formatEnumLabel(subscription.interval);
  return subscription.pendingInterval ? `${current} → ${formatEnumLabel(subscription.pendingInterval)}` : current;
}

/** A mandate's terms on one line: the amount as the household saw it, how often, and a first charge that differed. */
export function formatMandateTerms(mandate: {
  amountDisplay: string;
  firstChargeDisplay?: string | null;
  frequency: string;
}): string {
  // Rows from before the column widened still store the enum spelling; lower
  // it as this panel always rendered it. Every newer value is stored evidence
  // in the price's own vocabulary ('month', 'every 3 months') — verbatim.
  const frequency =
    mandate.frequency === 'MONTHLY' ? 'monthly' : mandate.frequency === 'ANNUAL' ? 'annual' : mandate.frequency;
  const terms = `${mandate.amountDisplay}, ${frequency}`;
  return mandate.firstChargeDisplay ? `${terms}, first charge ${mandate.firstChargeDisplay}` : terms;
}

/** The bank-account owner-name check's outcome, or null when no check ran. */
export function formatIdentityMatch(result?: IdentityMatchResult | null): string | null {
  return result ? formatEnumLabel(result) : null;
}

/** Who or what revoked a mandate. */
export function formatRevocationChannel(channel: MandateRevocationChannel): string {
  return formatEnumLabel(channel);
}

/** A link out to another mops tool, already scoped to the row it sits on. */
export interface IToolLink {
  label: string;
  to: string;
}

/**
 * A member's links into the Assistant tool and impersonation. Impersonate is
 * offered only to an active caregiver: impersonation refuses anyone else, after
 * the operator has already typed a reason.
 */
export function memberToolLinks(member: { id: string; status: UserStatus; familyRole: FamilyRole }): IToolLink[] {
  const links = [
    { label: 'Threads', to: userToolPath(PATHS.ASSISTANT_THREADS, member.id) },
    { label: 'Memory', to: userToolPath(PATHS.ASSISTANT_MEMORY, member.id) },
  ];
  return member.status === UserStatus.Active && member.familyRole === FamilyRole.Caregiver
    ? [...links, { label: 'Impersonate', to: userToolPath(PATHS.IMPERSONATE_USER, member.id) }]
    : links;
}

/**
 * Whose sign-up created the household: its earliest-enrolled caregiver.
 *
 * Derived rather than stored — nothing on `families` records an owner — so the
 * answer rests on the resolver's order (caregivers first, then oldest account
 * first, `id` breaking the tie), which is stable across two reads of one
 * household. Null for a household whose caregivers have all been tombstoned,
 * where naming a loved one as the owner would be a fabrication.
 */
export function householdOwnerId(linkedUsers: ReadonlyArray<{ id: string; familyRole: FamilyRole }>): string | null {
  return linkedUsers.find((member) => member.familyRole === FamilyRole.Caregiver)?.id ?? null;
}

/**
 * The household's links into the family-scoped tools. Each picks a user but
 * reads the whole household, so any member serves: the first one, and none for
 * a household with no user accounts.
 */
export function householdToolLinks(linkedUsers: ReadonlyArray<{ id: string }>): IToolLink[] {
  if (linkedUsers.length === 0) {
    return [];
  }
  const userId = linkedUsers[0].id;
  return [
    { label: 'Bank connections', to: userToolPath(PATHS.BANK_CONNECTIONS, userId) },
    { label: 'Bill management', to: userToolPath(PATHS.BILL_MANAGEMENT, userId) },
    { label: 'Work items', to: userToolPath(PATHS.WORK_ITEMS_LIST, userId) },
  ];
}

/** Whoever owes a work item, or the assistant when nobody in the household does. */
export interface IWorkItemAssigneeGroup<T> {
  /** Null for the assistant's own work, which is also how the group sorts last. */
  assigneeUserId: string | null;
  label: string;
  items: T[];
}

/**
 * Open work items gathered under whoever owes each one, household members
 * first and the assistant's own work last.
 *
 * Grouped rather than given an assignee column: a household's open work is
 * read one person at a time — what is on Ada's plate — and repeating the same
 * name down forty rows answers a question nobody asked. The assistant sorts
 * last because it is the pile an operator reads only after the people.
 *
 * Within a group the caller's order is preserved, which is newest first.
 */
export function groupWorkItemsByAssignee<T extends { assigneeUserId?: string | null; assigneeName?: string | null }>(
  items: readonly T[]
): IWorkItemAssigneeGroup<T>[] {
  const groups = new Map<string, IWorkItemAssigneeGroup<T>>();
  for (const item of items) {
    const assigneeUserId = item.assigneeUserId ?? null;
    // A null id is one group, not one per row, so it needs a key of its own.
    const key = assigneeUserId ?? '';
    const existing = groups.get(key);
    if (existing === undefined) {
      groups.set(key, {
        assigneeUserId,
        label: assigneeUserId === null ? 'Assistant' : (item.assigneeName ?? assigneeUserId),
        items: [item],
      });
      continue;
    }
    existing.items.push(item);
  }
  return [...groups.values()].sort((a, b) => {
    if ((a.assigneeUserId === null) !== (b.assigneeUserId === null)) {
      return a.assigneeUserId === null ? 1 : -1;
    }
    return a.label.localeCompare(b.label);
  });
}

/** What the outreach pass last did about a work item for its current assignee, and when. */
export interface IWorkItemLastNudge {
  outcome: 'sent' | 'skipped';
  at: string;
}

/**
 * The later of a work item's last delivered nudge and its last skipped one, or
 * null when its current assignee has had neither.
 *
 * The later one wins: a nudge sent last week followed by one skipped yesterday
 * reads as skipped, which is what explains why nothing arrived since.
 */
export function lastWorkItemNudge(item: {
  nudgeDispatchedAt?: string | null;
  nudgeDismissedAt?: string | null;
}): IWorkItemLastNudge | null {
  const sentAt = item.nudgeDispatchedAt ?? null;
  const skippedAt = item.nudgeDismissedAt ?? null;
  if (skippedAt !== null && (sentAt === null || Date.parse(skippedAt) > Date.parse(sentAt))) {
    return { outcome: 'skipped', at: skippedAt };
  }
  return sentAt === null ? null : { outcome: 'sent', at: sentAt };
}

/** One delivered message, with every work item it carried. */
export interface INotificationOutreachGroup<T> {
  outreachId: string;
  items: T[];
}

/**
 * Notification rows folded back into the messages that carried them.
 *
 * The read flattens each outreach across its work items, which is what the
 * table needs for one row per item, but it loses the fact that three rows were
 * one send. Grouping restores it without a second query: the rows arrive
 * newest-first and contiguous per outreach, so first-seen order is already
 * delivery order and needs no re-sort.
 */
export function groupNotificationsByOutreach<T extends { outreachId: string }>(
  notifications: readonly T[]
): INotificationOutreachGroup<T>[] {
  const groups = new Map<string, INotificationOutreachGroup<T>>();
  for (const notification of notifications) {
    const existing = groups.get(notification.outreachId);
    if (existing === undefined) {
      groups.set(notification.outreachId, { outreachId: notification.outreachId, items: [notification] });
      continue;
    }
    existing.items.push(notification);
  }
  return [...groups.values()];
}

/** A part of a household's account; the pane shows one at a time. */
export type AccountTab = 'household' | 'users' | 'banks' | 'billing' | 'workItems';

/** The pane's tabs, in strip order. */
export const ACCOUNT_TABS: ReadonlyArray<{ id: AccountTab; label: string }> = [
  { id: 'household', label: 'Household' },
  { id: 'users', label: 'Users' },
  { id: 'banks', label: 'Banks' },
  { id: 'billing', label: 'Billing' },
  { id: 'workItems', label: 'Work items' },
];

/** The tab a `?tab=` value names, or the household tab for a missing or unknown one. */
export function parseAccountTab(value: string | null): AccountTab {
  return ACCOUNT_TABS.find((tab) => tab.id === value)?.id ?? 'household';
}

/**
 * The query string with `tab` open, every other param kept. The household tab
 * drops `?tab=`, so a plain household link stays `?familyId=` alone.
 */
export function withAccountTab(params: URLSearchParams, tab: AccountTab): URLSearchParams {
  const next = new URLSearchParams(params);
  if (tab === 'household') {
    next.delete('tab');
  } else {
    next.set('tab', tab);
  }
  return next;
}
